import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronDown, Phone, Mail, MessageCircle } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { FAQ_CATEGORIES, FAQ_ITEMS } from "../i18n/faqData";
import { Reveal, SectionHeading, Sparkles } from "../components/ui-bits";

const ANSWER_LINKS = {
  nl: [
    ["online reserveren", "/reserveer"],
    ["reserveringsplatform", "/reserveer"],
    ["vooraf te reserveren", "/reserveer"],
    ["reserveren", "/reserveer"],
    ["openingsuren", "/contact"],
    ["menukaart", "/restaurant"],
    ["arrangementen", "/arrangementen"],
    ["kinder-discobowling", "/kinderfeestjes"],
    ["kinderfeestjes", "/kinderfeestjes"],
    ["discobowling", "/jenever-discobowling"],
  ],
  en: [
    ["book online", "/reserveer"],
    ["reservation platform", "/reserveer"],
    ["booking in advance", "/reserveer"],
    ["book", "/reserveer"],
    ["opening hours", "/contact"],
    ["menu", "/restaurant"],
    ["packages", "/arrangementen"],
    ["kids' disco bowling", "/kinderfeestjes"],
    ["kids' parties", "/kinderfeestjes"],
    ["disco bowling", "/jenever-discobowling"],
  ],
};

const renderAnswer = (text, lang) => {
  let counter = 0;
  let nodes = [text];
  for (const [kw, to] of ANSWER_LINKS[lang]) {
    nodes = nodes.flatMap((node) => {
      if (typeof node !== "string") return [node];
      const idx = node.toLowerCase().indexOf(kw);
      if (idx === -1) return [node];
      const match = node.slice(idx, idx + kw.length);
      return [
        node.slice(0, idx),
        <Link
          key={`lnk-${counter++}`}
          to={to}
          className="text-[#ff3b4d] font-semibold underline decoration-[#ff3b4d]/50 underline-offset-4 hover:text-white transition-colors"
        >
          {match}
        </Link>,
        node.slice(idx + kw.length),
      ];
    });
  }
  return nodes;
};

export default function Faq() {
  const { t, lang } = useLanguage();
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("all");
  const [open, setOpen] = useState(null);

  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FAQ_ITEMS.filter((it) => {
      const text = (lang === "en" ? `${it.qe} ${it.ae}` : `${it.qn} ${it.an}`).toLowerCase();
      return (!q || text.includes(q)) && (cat === "all" || it.cat === cat);
    });
  }, [query, cat, lang]);

  const catLabel = (id) => {
    const c = FAQ_CATEGORIES.find((x) => x.id === id);
    return c ? (lang === "en" ? c.en : c.nl) : id;
  };

  const count = (id) => FAQ_ITEMS.filter((it) => it.cat === id).length;

  return (
    <main className="relative pt-32 pb-24 overflow-hidden theme-red">
      <Sparkles count={12} />
      <div className="max-w-4xl mx-auto px-5">
        <SectionHeading eyebrow="Helios" title={t("faq.title")} sub={t("faq.sub")} />

        <Reveal>
          <div className="relative mb-6" data-testid="faq-search">
            <Search size={20} className="absolute left-5 top-1/2 -translate-y-1/2 text-[#ff3b4d]" />
            <input
              data-testid="faq-search-input"
              className="field pl-13 py-4 text-base rounded-full"
              style={{ paddingLeft: "3.2rem" }}
              placeholder={t("faq.search")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="flex flex-wrap gap-2 mb-10" data-testid="faq-categories">
            <button
              data-testid="faq-cat-all"
              onClick={() => { setCat("all"); setOpen(null); }}
              className={`font-display text-xs tracking-[0.2em] uppercase px-5 py-2.5 rounded-full border transition-all ${
                cat === "all"
                  ? "bg-[#ff3b4d] text-white border-[#ff3b4d] shadow-[0_0_20px_rgba(255,59,77,0.45)]"
                  : "border-[#ff3b4d]/35 text-[#9db8d6] hover:text-[#E2F1FF] hover:border-[#ff3b4d]/70"
              }`}
            >
              {t("faq.all")}
            </button>
            {FAQ_CATEGORIES.map((c) => (
              <button
                key={c.id}
                data-testid={`faq-cat-${c.id}`}
                onClick={() => { setCat(c.id); setOpen(null); }}
                className={`font-display text-xs tracking-[0.2em] uppercase px-5 py-2.5 rounded-full border transition-all ${
                  cat === c.id
                    ? "bg-[#ff3b4d] text-white border-[#ff3b4d] shadow-[0_0_20px_rgba(255,59,77,0.45)]"
                    : "border-[#ff3b4d]/35 text-[#9db8d6] hover:text-[#E2F1FF] hover:border-[#ff3b4d]/70"
                }`}
              >
                {lang === "en" ? c.en : c.nl} <span className="opacity-60">({count(c.id)})</span>
              </button>
            ))}
          </div>
        </Reveal>

        <div className="space-y-3" data-testid="faq-list">
          {items.length === 0 && (
            <p className="text-center text-[#9db8d6] py-10" data-testid="faq-no-results">{t("faq.noResults")}</p>
          )}
          {items.map((it, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={`${it.cat}-${i}`} delay={Math.min(i * 0.03, 0.3)}>
                <div data-testid={`faq-item-${i}`} className="glass glow-card rounded-2xl overflow-hidden">
                  <button
                    data-testid={`faq-question-${i}`}
                    onClick={() => setOpen(isOpen ? null : i)}
                    className="w-full flex items-center gap-4 text-left px-6 py-5"
                  >
                    <span className="flex-1 font-semibold text-[#E2F1FF]">{lang === "en" ? it.qe : it.qn}</span>
                    <span className="hidden sm:inline-block text-[10px] font-display tracking-[0.2em] uppercase px-3 py-1 rounded-full border border-[#ff3b4d]/40 text-[#ff3b4d] shrink-0">
                      {catLabel(it.cat)}
                    </span>
                    <ChevronDown
                      size={18}
                      className={`text-[#ff3b4d] shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  <div
                    className={`grid transition-all duration-300 ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
                  >
                    <div className="overflow-hidden">
                      <p className="px-6 pb-5 text-sm text-[#9db8d6] leading-relaxed" data-testid={`faq-answer-${i}`}>
                        {renderAnswer(lang === "en" ? it.ae : it.an, lang)}
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-14 glass rounded-3xl p-10 text-center" data-testid="faq-contact-block">
            <h3 className="font-display text-3xl uppercase neon">{t("faq.stillTitle")}</h3>
            <p className="mt-3 text-[#9db8d6] max-w-xl mx-auto">{t("faq.stillText")}</p>
            <div className="mt-7 flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:+3214812093" data-testid="faq-call-button" className="btn-neon font-display text-sm tracking-[0.2em] uppercase px-8 py-3.5 rounded-full inline-flex items-center gap-2 justify-center">
                <Phone size={16} /> +32 (0)14 81 20 93
              </a>
              <a href="mailto:heliosbowling@gmail.com" data-testid="faq-mail-button" className="btn-ghost font-display text-sm tracking-[0.2em] uppercase px-8 py-3.5 rounded-full inline-flex items-center gap-2 justify-center">
                <Mail size={16} /> heliosbowling@gmail.com
              </a>
            </div>
            <p className="mt-5 text-xs text-[#9db8d6] inline-flex items-center gap-2">
              <MessageCircle size={14} className="text-[#ff3b4d]" /> {t("faq.stillSocial")}
            </p>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
